import React, { createContext, useContext, useState, useEffect } from 'react';
import { STAGES, SOURCES, KPI_STATUSES, INSTALLMENT_TYPES, PAYMENT_METHODS, EXPENSE_CATEGORIES, getInstallmentName } from '../shared/shared-constants';
import { mapCustomer, mapLead as sharedMapLead, mapPayment, mapExpense, mapPackage, mapPromotion } from '../shared/shared-mappers';
import { usePaginatedFetch } from '../shared/usePaginatedFetch';

const SchoolContext = createContext();

export const useSchool = () => useContext(SchoolContext);

export const SchoolProvider = ({ children }) => {
    // Static State (Tier A)
    const [packages, setPackages] = useState([]);
    const [branches, setBranches] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [wpUsers, setWpUsers] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [loadingStatic, setLoadingStatic] = useState(true);

    const config = window.reactPress || window.rpVars || {};
    const API_BASE = (config.api?.rest_url || '/wp-json').replace(/\/$/, '') + '/sdt/v1';
    const nonce = config.api?.nonce || config.nonce;

    const headers = {
        'Content-Type': 'application/json',
        'X-WP-Nonce': nonce
    };

    // Paginated State (Tier B)
    const leadsData = usePaginatedFetch({ url: `${API_BASE}/leads`, headers, mapper: sharedMapLead, perPage: 50 });
    const customersData = usePaginatedFetch({ url: `${API_BASE}/customer`, headers, mapper: mapCustomer, perPage: 200 }); // 200 to populate dropdowns better
    const lessonsData = usePaginatedFetch({ url: `${API_BASE}/lessons`, headers, perPage: 50 });
    const paymentsData = usePaginatedFetch({ url: `${API_BASE}/payments`, headers, mapper: mapPayment, perPage: 50 });
    const expensesData = usePaginatedFetch({ url: `${API_BASE}/expenses`, headers, mapper: mapExpense, perPage: 50 });

    const fetchStaticData = async () => {
        const safeFetch = async (url, setter, mapper = null, fallback = []) => {
            try {
                const res = await fetch(url, { headers });
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) setter(mapper ? data.map(mapper).filter(Boolean) : data);
                    else setter(fallback);
                } else setter(fallback);
            } catch (err) { setter(fallback); }
        };

        try {
            await Promise.all([
                safeFetch(`${API_BASE}/packages`, setPackages, mapPackage),
                safeFetch(`${API_BASE}/branches`, setBranches),
                safeFetch(`${API_BASE}/instructor`, setInstructors),
                safeFetch(`${API_BASE}/settings/users`, setWpUsers),
                safeFetch(`${API_BASE}/promotions`, setPromotions, mapPromotion),
                (async () => {
                    try {
                        const res = await fetch(`${API_BASE}/me`, { headers });
                        if (res.ok) {
                            const data = await res.json();
                            setCurrentUser(data);
                        } else {
                            setCurrentUser(null);
                        }
                    } catch (err) {
                        setCurrentUser(null);
                    }
                })()
            ]);
        } finally {
            setLoadingStatic(false);
        }
    };

    const fetchData = () => {
        fetchStaticData();
        leadsData.refresh();
        customersData.refresh();
        lessonsData.refresh();
        paymentsData.refresh();
        expensesData.refresh();
    };

    useEffect(() => {
        fetchStaticData();
        // Initial paginated fetches
        leadsData.refresh();
        customersData.refresh();
        lessonsData.refresh();
        paymentsData.refresh();
        expensesData.refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Combine loading state, data mapping for backward compatibility
    const loading = loadingStatic || customersData.loading || leadsData.loading;
    const leads = leadsData.data;
    const customers = customersData.data;
    const lessons = lessonsData.data;
    const financials = {
        payments: paymentsData.data,
        expenses: expensesData.data
    };

    // Helper functions
    const getBranchId = (branchName) => {
        const branch = branches.find(b => b.branch_name === branchName || b.name === branchName);
        return branch ? branch.branch_id : null;
    };

    const getBranchName = (branchId) => {
        if (!branchId || !branches || branches.length === 0) return '';

        const bIdStr = String(branchId).toLowerCase().trim();

        // 1. Try matching by UUID (string) or Integer ID
        let branch = branches.find(b =>
            (b.branch_id && String(b.branch_id).toLowerCase().trim() === bIdStr) ||
            (b.id && String(b.id).toLowerCase().trim() === bIdStr)
        );

        // 2. If no ID match, check if the value itself IS a branch name
        if (!branch) {
            branch = branches.find(b =>
                (b.branch_name && b.branch_name.toLowerCase().trim() === bIdStr) ||
                (b.name && b.name.toLowerCase().trim() === bIdStr)
            );
        }

        return branch ? (branch.branch_name || branch.name) : '';
    };

    // Actions
    const addLead = async (lead) => {
        try {
            const names = (lead.name || '').split(' ');
            const payload = {
                first_name: names[0] || '',
                last_name: names.slice(1).join(' ') || '',
                phone_number: lead.phone,
                email: lead.email,
                lead_source: lead.source,
                branch_id: getBranchId(lead.branch),
                interest: lead.interest,
                status: lead.status || 'New',
                notes: lead.notes
            };

            const res = await fetch(`${API_BASE}/leads`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            if (res.ok) fetchData();
        } catch (error) {
            console.error('Error adding lead:', error);
        }
    };

    const updateLead = async (lead) => {
        try {
            const names = (lead.name || '').split(' ');
            const payload = {
                first_name: names[0] || '',
                last_name: names.slice(1).join(' ') || '',
                phone_number: lead.phone,
                email: lead.email,
                lead_source: lead.source,
                branch_id: getBranchId(lead.branch),
                interest: lead.interest,
                status: lead.status,
                notes: lead.notes,
                id: lead.id
            };
            const res = await fetch(`${API_BASE}/leads/${lead.id}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            if (res.ok) fetchData();
        } catch (error) {
            console.error('Error updating lead:', error);
        }
    };

    const addCustomer = async (customerData) => {
        try {
            const studentId = `SDT-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
            const payload = {
                first_name: customerData.firstName,
                last_name: customerData.surname,
                national_id__no: customerData.idNumber,
                gender: customerData.gender,
                email_address: customerData.email,
                phone_number: customerData.phone,
                student_id_number: studentId,
                package_id: customerData.packageId,
                branch_id: getBranchId(customerData.branch),
                customer_id: studentId,
                stage: customerData.stage,
                balance: customerData.balance,
                address: customerData.address,
                notes: customerData.notes,
                relationship_with_next_of_kin: customerData.nextOfKin?.relationship,
                next_of_kin_phone_number: customerData.nextOfKin?.phone,
                next_of_kin_name: customerData.nextOfKin?.name,
                enrollment_date: customerData.initialPaymentDate || new Date().toISOString().split('T')[0],
                lessons_remaining: customerData.lessonsRemaining
            };

            const res = await fetch(`${API_BASE}/customer`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const result = await res.json();
                fetchData();

                // Create user account if requested
                if (customerData.createAccount) {
                    try {
                        await createUserAccount(result.id);
                    } catch (e) {
                        console.error('Failed to create account during enrollment:', e);
                    }
                }

                if (customerData.initialPaymentAmount > 0) {
                    recordPayment({
                        student_id: studentId,
                        student_name: `${customerData.firstName} ${customerData.surname}`,
                        amount: customerData.initialPaymentAmount,
                        payment_date: new Date().toISOString(),
                        method: 'Cash',
                        description: 'Initial enrollment payment'
                    });
                }
            }
        } catch (error) {
            console.error('Error adding customer:', error);
        }
    };

    const updateCustomer = async (id, customerData) => {
        try {
            const payload = {
                first_name: customerData.firstName,
                last_name: customerData.surname,
                national_id__no: customerData.idNumber,
                gender: customerData.gender,
                email_address: customerData.email,
                phone_number: customerData.phone,
                package_id: customerData.packageId,
                branch_id: customerData.branchId || getBranchId(customerData.branch),
                instructor_id: customerData.instructorId,
                stage: customerData.stage,
                balance: customerData.balance,
                address: customerData.address,
                documents: JSON.stringify(customerData.documents || []),
                notes: customerData.notes,
                relationship_with_next_of_kin: customerData.nextOfKin?.relationship,
                next_of_kin_phone_number: customerData.nextOfKin?.phone,
                next_of_kin_name: customerData.nextOfKin?.name,
                lessons_remaining: customerData.lessonsRemaining,
                enrollment_date: customerData.enrollmentDate,
                date_of_birth: customerData.dateOfBirth,
                user_login: customerData.userLogin,
                id: id
            };

            const res = await fetch(`${API_BASE}/customer/${id}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                fetchData();
            }
        } catch (error) {
            console.error('Error updating customer:', error);
        }
    };

    const createUserAccount = async (customerId) => {
        try {
            const res = await fetch(`${API_BASE}/customer/${customerId}/create-user`, {
                method: 'POST',
                headers
            });
            if (res.ok) {
                const data = await res.json();
                fetchData();
                return data;
            } else {
                const err = await res.json();
                throw new Error(err.message || 'Failed to create user');
            }
        } catch (error) {
            console.error('Error creating user account:', error);
            throw error;
        }
    };

    const recordPayment = async (payment) => {
        try {
            // Map frontend fields to DB columns
            const payload = {
                customer_id: payment.customerId || payment.customer_id,
                amount: payment.amount,
                method: payment.method || 'Cash',
                payment_date: payment.date || payment.payment_date || new Date().toISOString(),
                description: payment.description || ''
            };

            const res = await fetch(`${API_BASE}/payments`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                fetchData();
            } else {
                const errorData = await res.json();
                console.error('Failed to record payment:', errorData);
                alert(`Error: ${errorData.message || 'Could not record payment'}`);
            }
        } catch (error) {
            console.error('Error recording payment:', error);
            alert('A network error occurred while recording the payment.');
        }
    };

    const addExpense = async (expense) => {
        try {
            // Map frontend fields to DB columns
            const payload = {
                category: expense.category,
                amount: expense.amount,
                method: expense.method || 'Cash',
                expense_date: expense.date ? new Date(expense.date).toISOString() : (expense.expense_date || new Date().toISOString()),
                description: expense.description || ''
            };

            console.log('Adding expense with payload:', payload);

            const res = await fetch(`${API_BASE}/expenses`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                console.log('Expense added successfully');
                fetchData();
            } else {
                const errorData = await res.json();
                console.error('Failed to add expense:', errorData);
                alert(`Error: ${errorData.message || 'Could not add expense'}`);
            }
        } catch (error) {
            console.error('Error adding expense:', error);
            alert('A network error occurred while adding the expense.');
        }
    };

    const updateExpense = async (id, expense) => {
        try {
            const payload = {
                category: expense.category,
                amount: expense.amount,
                method: expense.method || 'Cash',
                expense_date: expense.date ? new Date(expense.date).toISOString() : (expense.expense_date || new Date().toISOString()),
                description: expense.description || ''
            };

            const res = await fetch(`${API_BASE}/expenses/${id}`, {
                method: 'POST', // Simplified update via SDT_Base_Controller
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                fetchData();
            } else {
                const errorData = await res.json();
                console.error('Failed to update expense:', errorData);
            }
        } catch (error) {
            console.error('Error updating expense:', error);
        }
    };

    const updatePayment = async (id, payment) => {
        try {
            const payload = {
                customer_id: payment.customerId || payment.customer_id,
                amount: payment.amount,
                method: payment.method || 'Cash',
                payment_date: payment.date || payment.payment_date || new Date().toISOString(),
                description: payment.description || ''
            };

            const res = await fetch(`${API_BASE}/payments/${id}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                fetchData();
            } else {
                const errorData = await res.json();
                console.error('Failed to update payment:', errorData);
            }
        } catch (error) {
            console.error('Error updating payment:', error);
        }
    };

    // Constants
    const stages = STAGES;
    const sources = SOURCES;
    const kpiStatuses = KPI_STATUSES;
    const installmentTypes = INSTALLMENT_TYPES;

    const paymentMethods = PAYMENT_METHODS;

    const expenseCategories = EXPENSE_CATEGORIES;

    // Helper
    const getInstallmentName = (id) => installmentTypes.find(t => t.id === id)?.name || id;

    // Import Actions (Placeholders)
    const importLeads = async (data) => {
        console.log('Importing leads:', data);
        // Implement bulk import logic here
        return { imported: data.length, duplicates: 0, errors: 0 }; // Return mock success to prevent crash
    };

    const importCustomers = async (data, onProgress) => {
        console.log('Importing customers:', data);
        let imported = 0;
        let updated = 0;
        let errors = 0;

        for (let i = 0; i < data.length; i++) {
            try {
                const row = data[i];

                // Map CSV headers to expected format
                const customerData = {
                    firstName: row['First Name']?.trim() || '',
                    surname: row['Surname']?.trim() || '',
                    phone: row['Phone']?.trim() || '',
                    email: row['Email']?.trim() || '',
                    idNumber: row['ID Number']?.trim() || '',
                    gender: row['Gender']?.trim() || 'Male',
                    branch: row['Branch']?.trim() || branches[0]?.branch_name || '',
                    packageId: packages.find(p => p.name === row['Package']?.trim())?.id || packages[0]?.id,
                    initialPaymentAmount: parseFloat(row['Initial Payment']) || 0,
                    paymentPlan: installmentTypes[0]?.id,
                    source: sources[0],
                    stage: row['Current Stage']?.trim() || stages[0],
                    balance: parseFloat(row['Balance']?.replace(/[^0-9.]/g, '') || 0),
                    address: row['Address']?.trim() || '',
                    kpiStatus: kpiStatuses[0],
                    nextOfKin: { name: '', relationship: '', phone: '' },
                    initialPaymentDate: new Date().toISOString().split('T')[0],
                    notes: ''
                };

                // Check for duplicates by email or phone
                const existingCustomer = customers.find(c =>
                    (c.email_address && c.email_address === customerData.email) ||
                    (c.phone_number && c.phone_number === customerData.phone)
                );

                if (existingCustomer) {
                    // Update existing customer
                    await updateCustomer(existingCustomer.id, customerData);
                    updated++;
                } else {
                    // Create new customer
                    await addCustomer(customerData);
                    imported++;
                }

                // Update progress
                if (onProgress) {
                    onProgress(((i + 1) / data.length) * 100);
                }

            } catch (error) {
                console.error('Error importing customer:', error, data[i]);
                errors++;
            }
        }

        // Refresh data after import
        await fetchData();

        return { imported, updated, errors };
    };

    const importPayments = async (data, onProgress) => {
        console.log('Importing payments:', data);
        let imported = 0;
        let errors = 0;

        for (let i = 0; i < data.length; i++) {
            try {
                const row = data[i];
                const studentName = row['Student']?.trim().toLowerCase();

                // Find customer by name (first + last or just name)
                const customer = customers.find(c => {
                    const cName = c.name ? c.name.toLowerCase() : `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
                    return cName.includes(studentName) || studentName.includes(cName);
                });

                if (!customer) {
                    console.warn(`Customer not found for payment: ${row['Student']}`);
                    errors++;
                    continue; // Skip if no customer match
                }

                const paymentData = {
                    customerId: customer.id,
                    amount: parseFloat(row['Amount']?.replace(/[^0-9.]/g, '') || 0),
                    method: row['Payment Method']?.trim() || 'Cash',
                    date: row['Date'] ? new Date(row['Date']).toISOString() : new Date().toISOString(),
                    description: row['Description'] || `Payment for ${row['Package'] || 'course'}`
                };

                await recordPayment(paymentData);
                imported++;

                if (onProgress) {
                    onProgress(((i + 1) / data.length) * 100);
                }
            } catch (error) {
                console.error('Error importing payment:', error, data[i]);
                errors++;
            }
        }

        await fetchData();
        return { imported, duplicates: 0, errors };
    };

    const importExpenses = async (data, onProgress) => {
        console.log('Importing expenses:', data);
        let imported = 0;
        let errors = 0;

        for (let i = 0; i < data.length; i++) {
            try {
                const row = data[i];
                const expenseData = {
                    category: row['Category']?.trim() || 'General Office Maintenance',
                    amount: parseFloat(row['Amount']?.replace(/[^0-9.]/g, '') || 0),
                    method: row['Payment Method']?.trim() || 'Cash',
                    date: row['Date'] ? new Date(row['Date']).toISOString() : new Date().toISOString(),
                    description: row['Description'] || ''
                };

                await addExpense(expenseData);
                imported++;

                if (onProgress) {
                    onProgress(((i + 1) / data.length) * 100);
                }
            } catch (error) {
                console.error('Error importing expense:', error, data[i]);
                errors++;
            }
        }

        await fetchData();
        return { imported, errors };
    };

    const getTodaySummary = () => {
        try {
            const today = new Date().toDateString();

            const incomeToday = (financials.payments || [])
                .filter(p => p && p.payment_date && new Date(p.payment_date).toDateString() === today)
                .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

            const expenseToday = (financials.expenses || [])
                .filter(e => e && e.expense_date && new Date(e.expense_date).toDateString() === today)
                .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

            return { income: incomeToday, expense: expenseToday, balance: incomeToday - expenseToday };
        } catch (e) {
            console.error('Error calculating summary:', e);
            return { income: 0, expense: 0, balance: 0 };
        }
    };

    return (
        <SchoolContext.Provider value={{
            loading, leads, customers, financials, packages, branches, lessons, instructors, promotions,
            stages, sources, kpiStatuses, installmentTypes, paymentMethods, expenseCategories,
            wpUsers, currentUser,

            // Pagination exports
            loadMoreCustomers: customersData.loadMore,
            hasMoreCustomers: customersData.hasMore,
            totalCustomers: customersData.total,

            loadMoreLeads: leadsData.loadMore,
            hasMoreLeads: leadsData.hasMore,
            totalLeads: leadsData.total,

            loadMoreLessons: lessonsData.loadMore,
            hasMoreLessons: lessonsData.hasMore,
            totalLessons: lessonsData.total,

            loadMorePayments: paymentsData.loadMore,
            hasMorePayments: paymentsData.hasMore,

            loadMoreExpenses: expensesData.loadMore,
            hasMoreExpenses: expensesData.hasMore,

            getTodaySummary,
            getPackageName: (id) => {
                if (!id) return '';
                if (!id || typeof id === 'object') return '';
                return packages.find(p => String(p.id) === String(id))?.name || String(id);
            },
            getInstallmentName,
            getBranchId,
            getBranchName,
            addLead, updateLead, addCustomer, updateCustomer, recordPayment, updatePayment, addExpense, updateExpense,
            createUserAccount,
            importLeads, importCustomers, importPayments, importExpenses,
            refetch: fetchData
        }}>
            {children}
        </SchoolContext.Provider>
    );
};
